import { Injectable, Injector } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router'
import { Observable, firstValueFrom } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
/*
 * The `SyncGuard` can be used to run multiple guards in sequence and wait for them to complete.
 * This is useful when you need to ensure that a guard only runs if the previous one has passed.
 * If a guard returns `false` or a `UrlTree`, the sequence will stop and that value will be returned.
 * The guards can be passed as an array in the `syncGuards` property of the route data.
 *
 * Example route configuration:
 * ...
 * canActivateChild: [SyncGuard],
 * data: {
 *   syncGuards: [GuardA, GuardB],
 * }
 * ...
 */
export class SyncGuard {
  constructor(private readonly injector: Injector) {}
  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    for (const value of route.data.syncGuards || []) {
      const guard: {
        canActivate: CanActivateFn
      } = this.injector.get(value)
      let result = await guard.canActivate(route, state)
      if (result instanceof Observable) {
        result = await firstValueFrom(result)
      }

      if (result === false || result instanceof UrlTree) {
        return result
      }
    }
    return true
  }

  async canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    for (const value of route.data.syncGuards || []) {
      const guard: {
        canActivateChild: CanActivateChildFn
      } = this.injector.get(value)
      let result = await guard.canActivateChild(route, state)
      if (result instanceof Observable) {
        result = await firstValueFrom(result)
      }

      if (result === false || result instanceof UrlTree) {
        return result
      }
    }
    return true
  }
}
